<template>
	<v-card v-if="breadcrumbs.length > 1" variant="text">
		<v-card-text class="pa-0">
			<v-breadcrumbs :items="breadcrumbs" class="py-2">
				<template #divider>
					<v-icon size="small">fal fa-chevron-right</v-icon>
				</template>
				<template #item="{ item }">
					<v-breadcrumbs-item :to="item.to" exact>
						{{
							te(item.title)
								? t(item.title, { sep: ' | ' }).toUpperCase()
								: item.title.toUpperCase()
						}}
					</v-breadcrumbs-item>
				</template>
			</v-breadcrumbs>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePersonStore } from '@/stores/modules/person'
import { useBuildingStore } from '@/stores/modules/building'
/*import { useRecordStore } from '@/stores/modules/record'*/
import { useCraftsmanTaskStore } from '@/stores/modules/craftsmanTask'
import { useDocumentStore } from '@/stores/modules/document'
import { useRoleStore } from '@/stores/modules/role'
import { useSyncStore } from '@/stores/modules/sync'
import { useReportStore } from '@/stores/modules/report'
import { useUnitStore } from '@/stores/modules/unit'
import { useReviewDocumentStore } from '@/stores/modules/reviewDocument.ts'
import { useI18n } from 'vue-i18n'
import { useDayJs } from '@/composables/dateTime'

interface IIds {
	craftsmanTask?: number
	document?: number
	building?: number
	person?: number
	record?: number
	rolepermission?: number
	sync?: number
	report?: number
	unit?: number
	reviewDocument?: number
}

interface IBreadcrumbItem {
	id?: number
	name: string
	path: string
	to: string
	title: string
	params: Record<string, any>
	meta?: {
		title?: string
		icon?: string
	}
}

interface IStoreItem {
	id: number
	hp_id?: string | number
	subject?: string | null
	firstname?: string
	lastname?: string
	file_notes?: string
	address?: {
		street: string
		zip: string
		city: string
	}
	position?: string
	started_at?: string
	ended_at?: string
	name?: string
}

const { t, te } = useI18n()
const { getDateTime } = useDayJs()

const route = useRoute()
const router = useRouter()

// Initialize all stores
const personStore = usePersonStore()
const buildingStore = useBuildingStore()
/*const recordStore = useRecordStore()*/
const craftsmanTaskStore = useCraftsmanTaskStore()
const documentStore = useDocumentStore()
const roleStore = useRoleStore()
const syncStore = useSyncStore()
const reportStore = useReportStore()
const unitStore = useUnitStore()
const reviewDocumentStore = useReviewDocumentStore()

const breadcrumbs = ref<IBreadcrumbItem[]>([])
const ids = ref<IIds>({})

const items = computed(() => ({
	building: buildingStore.getItem(ids.value.building as number) as
		| IStoreItem
		| undefined,
	person: personStore.getItem(ids.value.person as number) as
		| IStoreItem
		| undefined,
	record: /*recordStore.getItem(ids.value.record as number) as
		| IStoreItem
		| */ undefined,
	craftsmanTask: craftsmanTaskStore.getItem(
		ids.value.craftsmanTask as number
	) as IStoreItem | undefined,
	document: documentStore.getItem(ids.value.document as number) as
		| IStoreItem
		| undefined,
	rolepermission: roleStore.getItem(ids.value.rolepermission as number) as
		| IStoreItem
		| undefined,
	sync: syncStore.getItem(ids.value.sync as number) as IStoreItem | undefined,
	report: reportStore.getItem(ids.value.report as number) as
		| IStoreItem
		| undefined,
	unit: unitStore.getItem(ids.value.unit as number) as IStoreItem | undefined,
	reviewDocument: reviewDocumentStore.getItem(
		ids.value.reviewDocument as number
	) as IStoreItem | undefined,
}))

const getRouteId = (type: string): number | undefined => {
	switch (type) {
		case 'record':
			return route.params?.recordId
				? parseInt(route.params.recordId as string)
				: undefined
		case 'unit':
			return route.params?.unitId
				? parseInt(route.params.unitId as string)
				: undefined
		case 'craftsmanTask':
			return route.params?.craftsmanTaskId
				? parseInt(route.params.craftsmanTaskId as string)
				: undefined
		case 'reviewDocument':
			return route.params?.reviewDocumentId
				? parseInt(route.params.reviewDocumentId as string)
				: undefined
		default:
			return route.params?.id ? parseInt(route.params.id as string) : undefined
	}
}

const formatTitle = (type: string, data: IStoreItem): string => {
	if (!data || Object.keys(data).length <= 0) return ''

	switch (type) {
		case 'craftsmanTask':
			return data.subject || ''
		case 'record':
			return data.hp_id?.toString() || ''
		case 'person':
			return `${data.firstname} ${data.lastname}`
		case 'document':
			return data.file_notes || ''
		case 'building':
			if (!data.address) return ''
			if (!data.address.street && !data.address.city) return ''
			return `${data.address.street}, ${data.address.zip} ${data.address.city}`
		case 'reviewDocument':
		case 'rolepermission':
			return `${data.hp_id} - ${data.name}`
		case 'sync': {
			const startedAt = getDateTime(data.started_at)?.dateTimeLocal
			const endedAt = getDateTime(data.ended_at)?.dateTimeLocal
			return endedAt ? `${startedAt} - ${endedAt}` : (startedAt ?? '')
		}
		case 'unit':
			return data.position || ''
		case 'report':
			return data.id.toString()
		default:
			return ''
	}
}

const updateRouteIds = (matchedRoute: any, name: string): void => {
	if (name === 'unit') {
		ids.value.unit = getRouteId('unit')
	} else if (matchedRoute.path === 'buildings/:id') {
		//name = 'building'
	} else {
		switch (name) {
			case 'building':
			case 'document':
			case 'person':
			case 'report':
			case 'sync':
				ids.value[name as keyof IIds] = getRouteId(name)
				break
			case 'record':
				ids.value.record = getRouteId('record')
				break
			case 'craftsmanTask':
				ids.value.craftsmanTask = getRouteId('craftsmanTask')
				break
			case 'rolepermission':
				ids.value.rolepermission = getRouteId('rolepermission')
				break
			case 'reviewDocument':
				ids.value.reviewDocument = getRouteId('reviewDocument')
				break
		}
	}
}

const updateItemTitle = (type: string, crumb: IBreadcrumbItem): void => {
	if (!crumb) return

	const index = breadcrumbs.value.findIndex(
		(x) => (x.name === type || !x.name) && x.id === crumb.id
	)

	if (index === -1) return

	const item = breadcrumbs.value[index]
	const itemData = items.value[type as keyof typeof items.value]
	if (!itemData) return

	const title = formatTitle(type, itemData)

	if (title) {
		breadcrumbs.value[index] = {
			...item,
			title,
			path: item.path.replaceAll(':id', crumb.id?.toString() || ''),
			params: crumb.params,
		}
	}
}

const buildBreadcrumbItem = (
	matchedRoute: any,
	name: string
): IBreadcrumbItem => {
	const id = getRouteId(name)
	return {
		id,
		name,
		path: matchedRoute.path,
		to: matchedRoute.path.replace(':id', id?.toString() || ''),
		title:
			matchedRoute.meta?.breadcrump ||
			matchedRoute.meta?.title ||
			(matchedRoute.meta as any)?.parent?.title ||
			'',
		params: route.params,
	}
}

const buildBreadcrumbs = (): void => {
	breadcrumbs.value = [
		{
			name: 'dashboard',
			path: '/dashboard',
			to: '/dashboard',
			title: 'Start',
			params: {},
			meta: {
				title: 'Start',
				icon: 'dashboard',
			},
		},
	]

	route.matched.forEach((matchedRoute) => {
		let name =
			(matchedRoute.name as string) ||
			(matchedRoute.meta?.name as string) ||
			(router.currentRoute.value?.name as string)

		updateRouteIds(matchedRoute, name)

		if (
			!breadcrumbs.value.find((x) => x.name === name) &&
			name &&
			matchedRoute.path !== '' &&
			!name?.includes('_group')
		) {
			const item = buildBreadcrumbItem(matchedRoute, name)
			breadcrumbs.value.push(item)
			updateItemTitle(name, item)
		}
	})
}

watch(
	items,
	(newItems) => {
		Object.entries(newItems).forEach(([key, value]) => {
			if (value && ids.value[key as keyof IIds]) {
				const crumb = breadcrumbs.value.find((x) => x.name === key)
				if (crumb) {
					updateItemTitle(key, crumb)
				}
			}
		})
	},
	{ deep: true }
)

watch(
	ids,
	(newIds) => {
		Object.entries(newIds).forEach(([key, value]) => {
			if (value) {
				const crumb = breadcrumbs.value.find((x) => x.name === key)
				if (crumb) {
					updateItemTitle(key, crumb)
				}
			}
		})
	},
	{ deep: true }
)

watch(
	() => route.params,
	() => {
		buildBreadcrumbs()
	},
	{ deep: true, immediate: true }
)

watch(
	() => route.matched,
	() => {
		buildBreadcrumbs()
	},
	{ deep: true }
)

onMounted(() => {
	buildBreadcrumbs()
})
</script>
